<script setup lang="ts">
import type { ButtonProps } from '@nuxt/ui'
import { onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { SESSION_EXPIRATION_MINUTES, useAuthStore } from '@/stores/auth'
import { useDashboards } from '@/stores/dashboards'
import { getStytchClient, getSessionInfo } from '@/lib/stytch'
import { B2BProducts } from '@stytch/vanilla-js'
import type {
  Member as StytchMember,
  Organization as StytchOrganization,
} from '@stytch/vanilla-js/b2b'
import { useColorMode } from '@vueuse/core'
import { handleErrorCallback, handleError } from '@/lib/error'
import logoUrl from '@/assets/logo.png'
import { usePostHog } from '@/lib/posthog'

const router = useRouter()
const route = useRoute()
const auth = useAuthStore()
const dashboards = useDashboards()
const colorMode = useColorMode()
const { posthog, GROUP_NAME } = usePostHog()
const props = defineProps<{
  mode: 'default' | 'invite'
}>()

const stytchContainerId = 'stytch-sdk'
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || ''

const redirectURI = route.query.redirect as string | undefined

const links =
  props.mode === 'default'
    ? ([
        {
          label: 'Learn more',
          href: 'https://ginjer.ai/',
          trailingIcon: 'i-lucide-external-link',
          target: '_blank',
          color: 'neutral',
          variant: 'subtle',
        },
      ] satisfies ButtonProps[])
    : undefined

const ADMIN_EMAIL_DOMAIN = 'ginjer.ai'

function postLogin(token: string, member: StytchMember, organization: StytchOrganization) {
  const isAdmin =
    member.roles.find((role) => role.role_id === 'stytch_super_admin') !== undefined ||
    member.email_address.endsWith(`@${ADMIN_EMAIL_DOMAIN}`)
  auth.setSession(token, isAdmin, organization, member)

  // Record login event (fire-and-forget)
  fetch(`${BACKEND_URL}/api/auth/login-event`, {
    method: 'POST',
    credentials: 'include',
    headers: { Authorization: `Bearer ${token}` },
  }).catch(() => {})

  if (isAdmin) {
    // admins/devs never send events, on any *.ginjer.ai
    posthog.opt_out_capturing()
  } else {
    posthog.opt_in_capturing()
    posthog.group(GROUP_NAME, organization.organization_name)
    posthog.identify(member.member_id, {
      email: member.email_address,
      name: member.name,
    })
    dashboards.fetchDashboards().catch(handleErrorCallback('Failed to fetch dashboards'))
  }
  if (redirectURI) {
    router.push(redirectURI)
  } else {
    router.replace({ name: isAdmin ? 'admin' : 'home' })
  }
}

onMounted(() => {
  const sessionInfo = getSessionInfo()
  const stytch = getStytchClient()

  // add listener to logout user when their session is revoked
  const unsubscribeLogoutListener = stytch.member.onChange((member) => {
    if (!member) router.replace({ name: 'login' })
  })
  // cleanup
  window.addEventListener('beforeunload', () => {
    if (unsubscribeLogoutListener) unsubscribeLogoutListener()
  })

  if (sessionInfo && sessionInfo.member && sessionInfo.tokens && sessionInfo.organization) {
    console.log('User is already logged in')
    postLogin(sessionInfo.tokens.session_token, sessionInfo.member, sessionInfo.organization)
    return
  }

  stytch.mount({
    elementId: `#${stytchContainerId}`,
    config: {
      authFlowType: props.mode === 'invite' ? 'PasswordReset' : 'Discovery',
      products: [B2BProducts.passwords, B2BProducts.sso],
      directLoginForSingleMembership: {
        status: true,
        ignoreInvites: true,
        ignoreJitProvisioning: true,
      },
      sessionOptions: {
        sessionDurationMinutes: SESSION_EXPIRATION_MINUTES,
      },
    },
    callbacks: {
      onEvent: async ({ type, data }) => {
        console.log('Stytch event:', type)

        if (
          type == 'B2B_DISCOVERY_INTERMEDIATE_SESSION_EXCHANGE' ||
          type == 'B2B_PASSWORD_RESET_BY_EMAIL' ||
          type == 'B2B_IMPERSONATION_AUTHENTICATE' ||
          type == 'B2B_SSO_AUTHENTICATE'
        ) {
          postLogin(data.session_token, data.member, data.organization)
        }
      },
      onError: (err: Error) => {
        if (
          err.message.includes('SDKAPIUnreachableError') ||
          err.message.includes('StytchSDKSchemaError') ||
          err.message.includes('StytchSDKUsageError')
        ) {
          // this is a technical error
          handleError(err, 'Please try again later.', "We couldn't log you in...")
        } else {
          // this is likely a validation error
          console.info('Stytch error:', err)
        }
      },
    },
    styles: {
      fontFamily: 'var(--default-font-family)',
      logo: {
        logoImageUrl: logoUrl,
      },
      colors: {
        primary: colorMode.value === 'dark' ? 'var(--ui-text-inverted)' : 'var(--ui-text)',
        secondary: colorMode.value === 'dark' ? 'var(--ui-text-inverted)' : 'var(--ui-text)',
      },
      buttons: {
        primary: {
          backgroundColor: 'var(--ui-primary)',
          textColor: 'var(--ui-text-inverted)',
          borderColor: 'var(--ui-primary)',
          borderRadius: 'calc(var(--ui-radius) * 1.5)',
        },
      },
      inputs: {
        textColor: colorMode.value === 'dark' ? 'var(--ui-text-inverted)' : 'var(--ui-text)',
        placeholderColor: 'var(--ui-text-dimmed)',
        borderColor: 'var(--ui-border-accented)',
        borderRadius: 'calc(var(--ui-radius) * 1.5)',
      },
      container: {
        backgroundColor:
          colorMode.value === 'dark'
            ? 'var(--ui-color-neutral-300)'
            : 'var(--ui-color-neutral-100)',
        borderColor:
          colorMode.value === 'dark'
            ? 'var(--ui-color-neutral-300)'
            : 'var(--ui-color-neutral-100)',
      },
    },
    strings: {
      'login.title': 'Welcome back',
      'link.signupResetPassword': 'Reset password',
      'password.setNew.title':
        props.mode === 'invite' ? 'Create a password to continue' : 'Set a new password',
      // we do that to avoid giving too much information, for security purposes
      'error.memberPasswordNotFound': 'Invalid email or password',
      'error.noUserPassword': 'Invalid email or password',
    },
  })
})
</script>

<template>
  <UContainer>
    <UPageHero
      title="Ginjer AI"
      description="Login to access your dashboard. New to Ginjer AI? Click on the link below to learn how to go from Data Chaos to Paid Media Intelligence."
      orientation="horizontal"
      :links="links"
    >
      <div :id="stytchContainerId"></div>
    </UPageHero>
  </UContainer>
</template>
